import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogContent,
  IconButton,
  Radio,
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getAssociates } from '../../services/associate';
import {
  Search as SearchIcon,
  RestartAlt,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { exportAsPDF, exportAsExcel, exportAsCSV } from '../../utils/importExport';
import { showSnackbar } from '../../components/Snackbar';
import AlertModal from '../../components/AlertModal';


const FollowupReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    status: '',
    searchTerm: '',
    category: 'all',
  });
  const [reportData, setReportData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });
  const [loading, setLoading] = useState(false);

  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseURL}/api/globalMasterDetails?type=inquiryStatus`);
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          const leadStatuses = result.data.filter(
            (item) => item.details && item.details.stageType === 'Lead'
          );
          setStatuses(leadStatuses);
        }
      } catch (error) {
        console.error('Failed to fetch inquiry statuses:', error);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchStatuses();
  }, []);

  // Associate dropdown states with pagination
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (filters.fromDate) {
        const from = new Date(filters.fromDate);
        if (!isNaN(from)) params.append('fromDate', from.toISOString().split('T')[0]);
      }
      if (filters.toDate) {
        const to = new Date(filters.toDate);
        if (!isNaN(to)) params.append('toDate', to.toISOString().split('T')[0]);
      }
      if (filters.status && filters.status !== 'All Status') params.append('status', filters.status);
      if (filters.searchTerm) params.append('searchKey', filters.searchTerm);
      if (selectedAssociate && selectedAssociate.id) params.append('associateId', selectedAssociate.id);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);

      const response = await fetch(`${baseURL}/api/reports/followUpReport?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();

      if (result.status && result.data && Array.isArray(result.data.docs)) {
        setReportData(result.data.docs);
        setTotalDocs(result.data.totalDocs || 0);
      } else {
        setReportData([]);
        setTotalDocs(0);
      }
    } catch (error) {
      console.error('Error fetching followup report:', error);
      setReportData([]);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, selectedAssociate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReportData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchReportData]);



  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;

    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20,
      };

      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);

      let associates = [];
      let total = 0;

      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Debounced associate search
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();

    if (trimmedInput.length === 0) {
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        return;
      }
      return;
    }

    if (trimmedInput.length < 2) {
      return;
    }

    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  const handleReset = () => {
    setFilters({
      fromDate: null,
      toDate: null,
      status: '',
      searchTerm: '',
      category: 'all',
    });
    setSelectedAssociate(null);
    setPage(0);
    setAssociateSearchInput('');
    setAssociatePage(1);
    setAssociateHasMore(true);
    associateSearchTermRef.current = '';
    fetchAssociates('', 1, false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Build rows shared by all export formats
  const buildExportRows = (dataToExport) => {
    const dataSource = dataToExport || reportData;
    return dataSource.map((followup, index) => ({
      'S.No': index + 1,
      'Date': followup.followUpDate ? new Date(followup.followUpDate).toLocaleDateString() : '-',
      'Customer Name': followup.customer || '-',
      'Contact': followup.mobileNumber || '-',
      'Category': followup.category === 'sitevisit' ? 'Site Visit' : followup.category === 'lead' ? 'Lead' : followup.category || '-',
      'Followup Type': followup.followUpType || '-',
      'Status': followup.inquiryStatus || '-',
      'Assigned To': followup.followedUpBy || '-',
      'Next Followup': followup.nextFollowUpDate ? new Date(followup.nextFollowUpDate).toLocaleDateString() : '-',
      'Remark': followup.followUpRemark || '-',
    }));
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate ? new Date(filters.fromDate).toISOString().split('T')[0] : '');
    setExportToDate(filters.toDate ? new Date(filters.toDate).toISOString().split('T')[0] : '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({ export: 'true' });

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (filters.status && filters.status !== 'All Status') params.append('status', filters.status);
        if (filters.searchTerm) params.append('searchKey', filters.searchTerm);
        if (selectedAssociate && selectedAssociate.id) params.append('associateId', selectedAssociate.id);
        if (filters.category && filters.category !== 'all') params.append('category', filters.category);

        // Date range based on selected mode
        if (exportDateMode === 'current') {
          if (filters.fromDate) {
            const from = new Date(filters.fromDate);
            if (!isNaN(from)) params.append('fromDate', from.toISOString().split('T')[0]);
          }
          if (filters.toDate) {
            const to = new Date(filters.toDate);
            if (!isNaN(to)) params.append('toDate', to.toISOString().split('T')[0]);
          }
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) params.append('fromDate', exportFromDate);
          if (exportToDate) params.append('toDate', exportToDate);
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            params.append('fromDate', exportFromDate);
            params.append('toDate', exportFromDate);
          }
        }
      }

      const response = await fetch(`${baseURL}/api/reports/followUpReport?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();

      let list = [];
      if (result.status && result.data && Array.isArray(result.data.docs)) {
        list = result.data.docs;
      } else if (result.success && Array.isArray(result.data)) {
        list = result.data;
      }

      if (list.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const rows = buildExportRows(list);
      const fileName = 'followup_report';

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        exportAsPDF({ rows, title: 'Follow-up Report', fileNameBase: fileName, orientation: 'l' });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Followup Report', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };


  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Follow-up Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage customer follow-ups
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
              variant="contained"
            />
            <RefreshButton
              onClick={() => fetchReportData()}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Follow-up Filters
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <DesktopDatePicker
                  label="From Date"
                  inputFormat="dd/MM/yyyy"
                  value={filters.fromDate}
                  onChange={(date) => setFilters({ ...filters, fromDate: date })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DesktopDatePicker
                  label="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={filters.toDate}
                  onChange={(date) => setFilters({ ...filters, toDate: date })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    {statuses.map((statusItem) => (
                      <MenuItem key={statusItem._id} value={statusItem.details.statusName}>
                        {statusItem.details.statusName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={associateOptions}
                  getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                  value={selectedAssociate}
                  onChange={(event, newValue) => {
                    setSelectedAssociate(newValue);
                    setPage(0);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setAssociateSearchInput(newInputValue);
                      if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                        setAssociatePage(1);
                        setAssociateHasMore(true);
                        associateSearchTermRef.current = '';
                      }
                    }
                  }}
                  onOpen={() => {
                    if (associateOptions.length === 0 && !associateFetchingRef.current) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      fetchAssociates(associateSearchInput || '', 1, false);
                    }
                  }}
                  loading={loadingAssociates}
                  ListboxProps={{
                    onScroll: (event) => {
                      const listboxNode = event.currentTarget;
                      const scrollTop = listboxNode.scrollTop;
                      const scrollHeight = listboxNode.scrollHeight;
                      const clientHeight = listboxNode.clientHeight;
                      const scrollBottom = scrollTop + clientHeight;

                      if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                        if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                          const nextPage = associatePage + 1;
                          setAssociatePage(nextPage);
                          fetchAssociates(associateSearchInput || '', nextPage, true);
                        }
                      }
                    },
                    style: { maxHeight: '300px', overflow: 'auto' },
                  }}
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assigned To"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Search Section */}
            <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Follow-ups"
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  placeholder="Search by customer name, contact, type, assigned to..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                    <MenuItem value="sitevisit">Site Visit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<RestartAlt />}
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: '40px'
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {(filters.searchTerm || filters.status || selectedAssociate || filters.fromDate || filters.toDate || (filters.category && filters.category !== 'all')) && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon fontSize="small" />
            <strong>Search Results:</strong> {totalDocs} follow-up{totalDocs !== 1 ? 's' : ''} found
            {filters.searchTerm && ` matching "${filters.searchTerm}"`}
            {filters.status && filters.status !== 'All Status' && ` with status "${filters.status}"`}
            {selectedAssociate && ` assigned to "${selectedAssociate.name}"`}
            {filters.category && filters.category !== 'all' && ` in category "${filters.category === 'sitevisit' ? 'Site Visit' : 'Lead'}"`}
          </Typography>
        </Box>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Follow-up Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Phone</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Category</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Followup Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Next Followup</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Assigned To</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Remark</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : reportData.length > 0 ? (
                  reportData.map((row, index) => (
                    <TableRow
                      key={row._id || index}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.followUpDate ? new Date(row.followUpDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customer || '-'}</TableCell>
                      <TableCell>{row.mobileNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.category === 'sitevisit' ? 'Site Visit' : row.category === 'lead' ? 'Lead' : row.category || '-'}
                          size="small"
                          color={row.category === 'sitevisit' ? 'secondary' : 'primary'}
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.followUpType || '-'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.inquiryStatus || '-'}
                          size="small"
                          color={row.inquiryStatus === 'HOT' ? 'error' : row.inquiryStatus === 'WARM' ? 'warning' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.nextFollowUpDate ? new Date(row.nextFollowUpDate).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{row.followedUpBy || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.followUpRemark || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No follow-ups found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalDocs > 0 && (
            <TablePagination
              
              component="div"
              count={totalDocs}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Follow-up Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${new Date(filters.fromDate).toLocaleDateString()} → ${new Date(filters.toDate).toLocaleDateString()}`
                    : filters.fromDate ? `From ${new Date(filters.fromDate).toLocaleDateString()}` : filters.toDate ? `Till ${new Date(filters.toDate).toLocaleDateString()}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default FollowupReport;
